import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  ScreenLayout,
  AuthHeader,
  SocialProofBanner,
  Spinner,
  LoadingOverlay,
} from '../../shared';
import { useFlowGuard } from '../../shared/useFlowGuard';
import {
  emailAtom,
  firstNameAtom,
  messageAtom,
  phoneAtom,
} from '../../../store';
import { OtpInput } from '../../shared/OtpInput';
import { api, APIError } from '../../../../../../shared/api';
import { getHostName } from '../../../../../../shared/location';

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 0 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledTitle = styled.h1`
  margin: 1rem 0 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #232323;
`;

const StyledDescription = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #232323;
`;

const StyledResend = styled.p`
  margin: 1.5rem 0 0;
  font-size: 1rem;
  color: #232323;
  text-align: center;

  button {
    background: none;
    border: none;
    padding: 0;
    color: #0071bc;
    font-size: inherit;
    font-family: inherit;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
`;

const StyledError = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: #a62017;
`;

const StyledCodeSentText = styled.span`
  font-size: 0.875rem;
  color: #57534e;
  text-align: center;
  margin-top: 0.75rem;
`;

const concatOtp = (code: string[]) => code.join('');
const EMPTY_OTP = ['', '', '', '', '', ''];

export function OtpPage() {
  const ready = useFlowGuard({
    atoms: [messageAtom, phoneAtom],
    fallback: '/auth/create',
  });
  const navigate = useNavigate();
  const listing = globalThis.xprops.listing;
  const residents = globalThis.xprops.residents;
  const phone = useAtomValue(phoneAtom);
  const firstName = useAtomValue(firstNameAtom);
  const email = useAtomValue(emailAtom);
  const message = useAtomValue(messageAtom);
  const [otp, setOtp] = useState<string[]>(EMPTY_OTP);

  const handleBack = useCallback(() => {
    navigate('/auth/create');
  }, [navigate]);

  const handleClose = useCallback(() => {
    globalThis.xprops?.onToggleMessenger?.();
  }, []);

  const completeFlow = useMutation({
    mutationFn: async () => {
      return api.post('/api/consumer/phone_auth/complete', {
        phone_number: phone,
        phone_number_country: 'US',
        listing_id: listing.id,
        first_name: firstName,
        email: email,
        message_content: message,
      });
    },
    onSuccess: () => {
      navigate('/auth/success');
    },
  });

  const verifyOtp = useMutation({
    mutationFn: async (code: string[]) => {
      return api.post('/api/consumer/phone_auth/verify', {
        phone_number: phone,
        phone_number_country: 'US',
        code: concatOtp(code),
      });
    },
    onSuccess: () => completeFlow.mutate(),
  });

  const resendOtp = useMutation({
    mutationFn: async () => {
      const hostname = getHostName(globalThis.xprops.getParentURL());

      return api.put('/api/consumer/phone_auth/resend', {
        hostname,
        phone_number: phone,
        phone_number_country: 'US',
      });
    },
    onSuccess: () => {
      verifyOtp.reset();
      setOtp(EMPTY_OTP);
    },
  });

  const isVerifyRateLimited =
    verifyOtp.isError && (verifyOtp.error as APIError)?.status === 429;

  const isResendRateLimited =
    resendOtp.isError && (resendOtp.error as APIError)?.status === 429;

  const isRateLimited = isVerifyRateLimited || isResendRateLimited;

  const isVerifying = verifyOtp.isLoading || completeFlow.isLoading;

  const handleOtpChange = useCallback(
    (values: string[]) => {
      setOtp(values);
      if (verifyOtp.isError) verifyOtp.reset();
    },
    [verifyOtp]
  );

  const handleOtpComplete = useCallback(
    async (values: string[]) => {
      try {
        await verifyOtp.mutateAsync(values);
      } catch {
        setOtp(EMPTY_OTP);
      }
    },
    [verifyOtp]
  );

  if (!ready) return null;

  return (
    <ScreenLayout
      header={<AuthHeader onBack={handleBack} onClose={handleClose} />}
      footer={
        <SocialProofBanner
          count={residents.length || 34}
          residents={residents}
          listingName={listing?.name ?? ''}
        />
      }
    >
      {isVerifying && <LoadingOverlay label="Verifying" />}
      <StyledBody>
        <StyledTitle>Enter code</StyledTitle>
        <StyledDescription>
          We sent a 6-digit code to {phone}.
        </StyledDescription>
        <OtpInput
          values={otp}
          hasError={verifyOtp.isError}
          disabled={isVerifying || isRateLimited}
          onChange={handleOtpChange}
          onComplete={handleOtpComplete}
        />
        {isRateLimited ? (
          <StyledError>
            Number of attempts exceeded. Please try again later.
          </StyledError>
        ) : (
          (verifyOtp.isError || completeFlow.isError || resendOtp.isError) && (
            <StyledError>
              {(verifyOtp.error as Error)?.message ??
                (completeFlow.error as Error)?.message ??
                (resendOtp.error as Error)?.message ??
                'Incorrect code. Please try again.'}
            </StyledError>
          )
        )}
        {!isVerifying && (
          <StyledResend>
            {!verifyOtp.isError && !isRateLimited && `Didn't receive a code? `}
            <button
              type="button"
              disabled={resendOtp.isLoading}
              onClick={() => resendOtp.mutate()}
            >
              {resendOtp.isLoading ? (
                <Spinner size="1rem" color="#0071bc" />
              ) : (
                'Resend code'
              )}
            </button>
          </StyledResend>
        )}

        {resendOtp.isSuccess && !verifyOtp.isError && !isVerifying && (
          <StyledCodeSentText>
            Code was resent to {`${phone}`}
          </StyledCodeSentText>
        )}
      </StyledBody>
    </ScreenLayout>
  );
}
